import React from "react";
import { StrategyType } from "@/lib/interfaces and Enums/ENUMStrategyType";
import {
  formatValueWithNotation,
  returnDecimalFormatting,
} from "@/lib/globalFormatterHelper";
import {
  returnFractionStringQuoteValue,
  returnFractionStringZValueDecimal,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { determineNotationCurrencySymbol } from "@/lib/currencySymbolHelper";
import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";

// A cover / coverSpread / competition value is "present" only when it is
// neither null/undefined nor an empty string. A removed value arrives as null
// OR "" (the backend clears it to an empty string), so "" must count as absent.
// This is the single source of truth for both cell display (blank when absent)
// and column visibility (hidden when absent) — keep the two in lockstep.
export function hasCoverValue(value: any): boolean {
  return value != null && value !== "";
}

// Mirrors the RP ER-level branch of formattedQuote.tsx — outright reads the
// leg's notation, everything else reads notationER on the trade root.
export function resolveCoverNotation(fullTradeData: any): string | undefined {
  const isOutright = fullTradeData?.strategyType === StrategyType.Outright;
  if (isOutright) return fullTradeData?.legs?.[0]?.notation;
  return fullTradeData?.notationER;
}

// String form for copy/paste — value only, NO notation symbol.
// Decimal places driven by notation via returnDecimalFormatting, matching
// quoteValueFormatter behaviour for bid/ask.
export function formatCoverValueForCopy(
  value: any,
  notation: string | undefined,
): string {
  if (!hasCoverValue(value)) return "";
  const valueStr = String(value);

  if (notation === "$fr" && valueStr.includes("-")) {
    return valueStr;
  }

  const num = Number(value);
  if (Number.isNaN(num)) return valueStr;
  return returnDecimalFormatting(num, notation as any, true);
}

// JSX for the cell renderer. Notation suffix/prepend composition is delegated
// to formatValueWithNotation — this function only handles the bits unique to
// cover/coverSpread: $fr in hyphen form (JSX fraction), the ER-level "$" →
// currency-symbol resolution, and notation-driven decimal rounding.
export function formatCoverValueForDisplay(
  value: any,
  notation: string | undefined,
  fullTradeData: any,
): React.ReactNode {
  if (!hasCoverValue(value)) return "";
  const valueStr = String(value);

  if (notation === "$fr" && valueStr.includes("-")) {
    return (
      <>
        {returnFractionStringQuoteValue(false, { value: valueStr })}
        {returnFractionStringZValueDecimal(false, { value: valueStr })}
      </>
    );
  }

  const num = Number(value);
  if (Number.isNaN(num)) return valueStr;
  const rounded = returnDecimalFormatting(num, notation as any);

  const resolvedNotation =
    notation === "$"
      ? determineNotationCurrencySymbol(fullTradeData, notation)
      : notation;

  return formatValueWithNotation(rounded, resolvedNotation);
}

export function coverCellValueFormatter(params: any): string {
  const fieldName: string | undefined =
    params.colDef?.cellRendererParams?.fieldName;
  if (!fieldName) return "";
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const value = (fullTradeData as any)?.[fieldName];
  if (!hasCoverValue(value)) return "";
  const notation = resolveCoverNotation(fullTradeData);
  return formatCoverValueForCopy(value, notation);
}
